// Swal component
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

const MySwal = withReactContent(Swal)

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = obj => Object.keys(obj).length === 0

// ** Returns K format from a number
export const kFormatter = num => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num)

// ** Converts HTML to string
export const htmlToString = html => html.replace(/<\/?[^>]+(>|$)/g, '')

// ** Checks if the passed date is today
const isToday = date => {
  const today = new Date()
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  )
}

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (value, formatting = { month: 'short', day: 'numeric', year: 'numeric' }) => {
  if (!value) return value
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value)
  let formatting = { month: 'short', day: 'numeric' }

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: 'numeric', minute: 'numeric' }
  }

  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => localStorage.getItem('userData')
export const getUserData = () => JSON.parse(localStorage.getItem('userData'))


/**
 ** This function is used to request a modal confirmation using MySwal Component
 * ? NOTE: Your application must have a translate hook for translating keys
 * @param {function} translateHook The function to pass for translating text
 * @param {string} translatedTitleText,translatedDescriptionText,translatedConfirmationAnswer1,translatedConfirmationAnswer2
 */
const confirmationTexts = {
    confirmationTitle : 'Confirmation Request',
    confirmationDescription: 'Are you sure to continue'
}

export const askConfirmationWithSwal = async (
    translateHook,
    translatedTitleText = undefined,
    translatedDescriptionText = undefined,
    translatedConfirmationAnswer1 = undefined,
    translatedConfirmationAnswer2 = undefined
) => {
    let mySwalResponse
    try {
        mySwalResponse = await MySwal.fire({
            title: translatedTitleText ? translatedTitleText : translateHook(confirmationTexts.confirmationTitle),
            text:  translatedDescriptionText ? translatedDescriptionText : translateHook(confirmationTexts.confirmationDescription),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: translatedConfirmationAnswer1 ? translatedConfirmationAnswer1 : translateHook("Yes"),
            cancelButtonText: translatedConfirmationAnswer2 ? translatedConfirmationAnswer2 : translateHook("No"),
            customClass: {
                confirmButton: 'btn btn-primary',
                cancelButton: 'btn btn-outline-danger ms-1'
            },
            buttonsStyling: false
        })
        if (mySwalResponse.value) {
            return Promise.resolve(true)
        } else {
            console.log("False")
            return Promise.resolve(false)
        }
    } catch (e) {
        return Promise.reject(e)
    }
}


/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = userRole => {
  if (userRole === 'admin') return '/'
  if (userRole === 'client') return '/access-control'
  return '/login'
}


// ** React Select Theme Colors
export const selectThemeColors = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: '#7367f01a', // for option hover bg-color
    primary: '#7367f0', // for selected option bg-color
    neutral10: '#7367f0', // for tags bg-color
    neutral20: '#ededed', // for input border-color
    neutral30: '#ededed' // for input hover border-color
  }
})

/**
 * Convert all numbers to localeFormat with Intl and maximum 2 decimals
 * @param number
 * @param min_decimal
 * @param language
 * @param max_decimal
 *
 * */
export const toLocaleNumber = (number, min_decimal = 2, language = undefined, max_decimal = 2) => {
    return new Intl.NumberFormat(language, {minimumFractionDigits: min_decimal, maximumFractionDigits: max_decimal}).format(number)
}

/**
 * Convert a string to its representational float
 * @param {string} string to convert
 * care => parseFloat should be try catched
 * */
export const toInternationalNumber = (string) => {
    return parseFloat(string.replace(',', '.')).toFixed(2);
}

/**
 * Translate all the labels in kendo grid. Use it in useEffect with the i18n dependency
 *
 * @param {HTMLElement} gridRef The grid target taken with useref()
 * @param {Function} translationHook the hook from useTranslation
 *
 * */
export const CHANGE_KENDO_GRID_LABELS = (gridRef, translationHook) => {
    if (gridRef.current) {
        const icons = gridRef.current.element.querySelectorAll('.k-grid-column-menu.k-grid-filter');
        const td_ex = gridRef.current.element.querySelectorAll('td[aria-expanded="false"]');
        const td = gridRef.current.element.querySelectorAll('td[aria-expanded="true"]');
        if (icons.length) icons.forEach(node => node.setAttribute('title', `${translationHook('Filter')}`))

        // use this if when kendo grid has groups
        if (td_ex.length) {
            td_ex.forEach(node => {
                if (node && node.querySelector('.k-i-collapse.k-icon'))
                    node.querySelector('.k-i-collapse.k-icon').setAttribute('title', `${translationHook('Less Info')}`)
                else if (node && node.querySelector('.k-i-expand.k-icon'))
                    node.querySelector('.k-i-expand.k-icon').setAttribute('title', `${translationHook('More Info')}`)
                else node.setAttribute('title', `${translationHook('More Info')}`)
            })
        }
        if (td.length) {
            td.forEach(node => {
                if (node && node.querySelector('.k-i-collapse.k-icon'))
                    node.querySelector('.k-i-collapse.k-icon').setAttribute('title', `${translationHook('Less Info')}`)
                else if(node && node.querySelector('.k-i-expand.k-icon'))
                    node.querySelector('.k-i-expand.k-icon').setAttribute('title', `${translationHook('More Info')}`)
                else node.setAttribute('title', `${translationHook('Less Info')}`)
            })
        }
    }
}

/**
 * Return the localized date
 * @returns {string}
 * @param date
 * @param language
 * @param withoutTime = Specify only date options Year, month, day
 */
export const toLocaleDate = (date, language = undefined, withoutTime = false) => {
    let options = {
        day: '2-digit',
        month: "2-digit",
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }
    if (withoutTime) {
        delete options.hour
        delete options.minute
        delete options.second
    }
    return new Date(date).toLocaleDateString(
        language,
        options
    )
}
/**
 ============ FORMS ==================
 Handlers and rules for <input/> forms
 =====================================
 **/
export const DECIMAL_FIELD_RULES = (translationHook, language = undefined, required = true) => {
    return {
        required: {
            value: required,
            message: translationHook("This field is required")
        },
        pattern: {
            value: (() => {
                if (language === 'it') {
                    return /^-?[0-9]{1,3}(?:[0-9]{3})*(?:\,[0-9]{1,})?$/
                }

                return /^-?[0-9]{1,3}(?:[0-9]{3})*(?:\.[0-9]{1,})?$/
            })(),
            message: translationHook("This is a not valid float")
        }
    }
}

export const DECIMAL_FIELD_RULES_NO_ZERO = (translationHook, language = undefined, required = true) => {
    return {
        required: {
            value: required,
            message: translationHook("This field is required")
        },
        pattern: {
            value: (() => {
                if (language === 'it') {
                    return /^([1-9]\d*(\,\d+)?|0+\,\d*[1-9]\d*)$/
                }

                return  /^([1-9]\d*(\.\d+)?|0+\.\d*[1-9]\d*)$/
            })(),
            message: translationHook("Insert a valid float number, greater than 0")
        }
    }
}

export const INTEGER_FIELD_RULES = (translationHook, language = undefined, required = true) => {
        return {
        required: {
            value: required,
            message: translationHook("This field is required")
        },
        pattern: {
            value: (() => {
                return /^\d*(\d+)?$/
            })(),
            message: translationHook("This is a not valid integer")
        },
        validate: {
             noZero: (v) => /^(([1-9]\d*)|([0]{1}))(\.\d+)?$/.test(v) || translationHook('Insert a valid integer number')
        }
    }
}

export const INTEGER_POSITIVE_FIELD_RULES = (
    translationHook,
    language = undefined,
    required = true
) => {
        return {
        required: {
            value: required,
            message: translationHook("This field is required")
        },
        pattern: {
            value: (() => {
                return /^\d*(\d+)?$/
            })(),
            message: translationHook("This is a not valid integer")
        },
        validate: {
            positive: (v) => /^(([1-9]\d*)|(([0]{1}))(\.\d+))?$/.test(v) || translationHook('Insert a valid positive integer number'),
        }
    }
}

export const PERCENTAGE_FIELD_RULES = (translationHook, language = undefined, required = true) => {
    return {
        required: {
            value: required,
            message: translationHook("This field is required")
        },
        pattern: {
            value: (() => {
                if (language === 'it') {
                    return /(^100(\,0{1,2})?$)|(^([1-9]([0-9])?|0)(\,[0-9]{1,})?$)/
                }

                return /(^100(\.0{1,2})?$)|(^([1-9]([0-9])?|0)(\.[0-9]{1,})?$)/
            })(),
            message: translationHook("Not a valid percentage")
        }
    }
}

export const DECIMAL_NUMBER_CONVERTER = (value, max = 2) => {
    return parseFloat(
        parseFloat(value
          .replace(/[^\d\-.,]/g, "")
          .replace(/,/g, ".")
          .replace(/\.(?=.*\.)/g, "")
        ).toFixed(max));
}

export const DECIMAL_NUMBER_RECOVERTER = (value, language = undefined) => {
    if (language === 'it') return (value.replace(/[\.]/g, ""))
    else return (value.replace(/[\,]/g, ""))
}

export const ALLOW_ONLY_DECIMAL = (value, language = undefined) => {
    if (language === 'it') return value.replace(/[^0-9,]/g, '')
    return value.replace(/[^0-9.]/g, '')
}

export const ALLOW_ONLY_INTEGER = (value) => {
    return value.replace(/[^0-9]/g, '')
}

 /**
   * Return olympus-fronend based validation errors [TRANSLATED].
   * @param  {responseData} response from API handler.
   * @return {Array[ {name: '', msg: ''} ] || String }
   */
export const settingAPIValidationErrors = (responseData) => {
        const details = responseData.detail;
        let formatted_errors = []

        if (typeof details === 'string')
            return details

        if (Array.isArray(details)) {
            // Build up formatted_errors objects
            formatted_errors = details.map((detailLocation) => {
                return {name: detailLocation.loc, msg: ''}
            })
            details.forEach((validationField) => {
                // Msg can be array
                if (Array.isArray(validationField.msg)) {
                    formatted_errors.find((detailLocation) =>
                        detailLocation.name === validationField.loc)
                        .msg = validationField.msg.join(' ')
                } else
                    formatted_errors.find((detailLoc) =>
                        detailLoc.name === validationField.loc)
                        .msg = validationField.msg
            })
        }
        return formatted_errors
    }